import { useMemo, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  useTemplates,
  useDeleteTemplate,
  useCloneTemplate,
  type Template,
  type TemplateCategory,
  type TemplateKind,
} from '../../api/templates.api';
import { useToast } from '../../components/ui/Toast';
import { useAuthStore } from '../../stores/auth.store';
import { LayoutPickerModal } from './LayoutPickerModal';

/* ── Templates gallery (replaces the Sprint 13 table view) ──
   - Card grid with iframe thumbnails of body_html.
   - Filters by category, kind, owner.
   - Per-card actions: Preview, Use, Customise (system), Duplicate, Delete (own). */

type OwnerTab = 'all' | 'mine' | 'system';

const ALL_CATEGORIES: TemplateCategory[] = [
  'CANDIDATE_ENGAGEMENT',
  'BUSINESS_DEVELOPMENT',
  'JOB_CAMPAIGN',
  'DEAL_CAMPAIGN',
  'WEBINAR_EVENT',
  'NEWSLETTER',
];

const KIND_LABELS: Record<TemplateKind, string> = {
  CONTENT_TEMPLATE: 'Templates',
  LAYOUT_STRUCTURE: 'Layouts',
  CUSTOM_HTML: 'Custom HTML',
};

export interface GalleryFilters {
  ownerTab: OwnerTab;
  categories: Set<TemplateCategory>;
  kinds: Set<TemplateKind>;
  search: string;
  meUserId: string;
}

/** Pure filter — exported so tests can verify behaviour without rendering. */
export function applyGalleryFilters(rows: Template[], f: GalleryFilters): Template[] {
  const q = f.search.trim().toLowerCase();
  return rows.filter((t) => {
    if (f.ownerTab === 'mine' && (t.is_system || t.created_by !== f.meUserId)) return false;
    if (f.ownerTab === 'system' && !t.is_system) return false;
    if (f.categories.size > 0 && (!t.category || !f.categories.has(t.category))) return false;
    if (f.kinds.size > 0 && !f.kinds.has(t.kind)) return false;
    if (q && !`${t.name} ${t.subject}`.toLowerCase().includes(q)) return false;
    return true;
  });
}

export function TemplatesPage() {
  const navigate = useNavigate();
  const me = useAuthStore((s) => s.user?.user_id ?? '');
  const { addToast } = useToast();
  const { data, isLoading, isError, refetch } = useTemplates();
  const del = useDeleteTemplate();
  const clone = useCloneTemplate();

  const [ownerTab, setOwnerTab] = useState<OwnerTab>('all');
  const [categories, setCategories] = useState<Set<TemplateCategory>>(new Set());
  const [kinds, setKinds] = useState<Set<TemplateKind>>(new Set(['CONTENT_TEMPLATE', 'LAYOUT_STRUCTURE', 'CUSTOM_HTML']));
  const [search, setSearch] = useState('');
  const [pickerOpen, setPickerOpen] = useState(false);
  const [previewing, setPreviewing] = useState<Template | null>(null);

  const rows = data?.data ?? [];
  const visible = useMemo(
    () => applyGalleryFilters(rows, { ownerTab, categories, kinds, search, meUserId: me }),
    [rows, ownerTab, categories, kinds, search, me],
  );

  const toggleCategory = (c: TemplateCategory) => {
    setCategories((prev) => {
      const next = new Set(prev);
      next.has(c) ? next.delete(c) : next.add(c);
      return next;
    });
  };
  const toggleKind = (k: TemplateKind) => {
    setKinds((prev) => {
      const next = new Set(prev);
      next.has(k) ? next.delete(k) : next.add(k);
      return next;
    });
  };

  const handleDelete = (t: Template) => {
    if (!window.confirm(`Delete template "${t.name}"?`)) return;
    del.mutate(t.id, {
      onSuccess: () => addToast({ variant: 'success', title: 'Template deleted' }),
      onError: () => addToast({ variant: 'danger', title: 'Delete failed' }),
    });
  };

  const handleCustomise = async (t: Template) => {
    try {
      const cloned = await clone.mutateAsync({ id: t.id });
      addToast({
        variant: 'success',
        title: `Created your copy of "${t.name}"`,
        description: 'System templates are read-only. You\'re now editing your own copy. Existing campaigns are unaffected.',
      });
      navigate(`/templates/${cloned.id}/edit`);
    } catch {
      addToast({ variant: 'danger', title: 'Could not create copy' });
    }
  };

  const handleDuplicate = async (t: Template) => {
    try {
      const cloned = await clone.mutateAsync({ id: t.id });
      addToast({ variant: 'success', title: `Duplicated as "${cloned.name}"` });
    } catch {
      addToast({ variant: 'danger', title: 'Duplicate failed' });
    }
  };

  const handleUseInCampaign = (t: Template) => {
    navigate(`/campaigns/new?template_id=${t.id}`);
  };

  // Own templates open the builder directly — editing one doesn't
  // affect any campaigns currently using it (campaigns snapshot the
  // body at sequence-step pick time; the next campaign that picks the
  // template gets the new version).
  const handleEditOwn = (t: Template) => {
    // Custom-HTML templates edit as code (block builder would mangle them).
    navigate(t.kind === 'CUSTOM_HTML' ? `/templates/html/${t.id}/edit` : `/templates/${t.id}/edit`);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
      <Breadcrumb />
      <HeadingRow
        total={rows.length}
        onNew={() => setPickerOpen(true)}
        onNewHtml={() => navigate('/templates/html/new')}
      />

      <FilterRow
        ownerTab={ownerTab}
        onOwnerTab={setOwnerTab}
        categories={categories}
        onToggleCategory={toggleCategory}
        kinds={kinds}
        onToggleKind={toggleKind}
        search={search}
        onSearch={setSearch}
      />

      {isError ? (
        <ErrorState onRetry={() => refetch()} />
      ) : isLoading ? (
        <LoadingGrid />
      ) : visible.length === 0 ? (
        <EmptyState onClear={() => { setOwnerTab('all'); setCategories(new Set()); setSearch(''); }} hasAny={rows.length > 0} />
      ) : (
        <Grid
          rows={visible}
          meUserId={me}
          onPreview={(t) => setPreviewing(t)}
          onUse={handleUseInCampaign}
          onCustomise={handleCustomise}
          onEditOwn={handleEditOwn}
          onDuplicate={handleDuplicate}
          onDelete={handleDelete}
        />
      )}

      <LayoutPickerModal open={pickerOpen} onClose={() => setPickerOpen(false)} />
      {previewing && <PreviewModal template={previewing} onClose={() => setPreviewing(null)} />}
    </div>
  );
}

/* ── Header ── */

function Breadcrumb() {
  return (
    <nav aria-label="Breadcrumb">
      <ol style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', listStyle: 'none', margin: 0, padding: 0 }}>
        <li>
          <Link to="/dashboard" style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)', textDecoration: 'none' }}>Engage</Link>
        </li>
        <li aria-hidden="true" style={{ color: 'var(--color-text-subtle)' }}>›</li>
        <li aria-current="page" style={{ fontSize: 'var(--text-sm)', fontWeight: 600, color: 'var(--color-text-secondary)' }}>Templates</li>
      </ol>
    </nav>
  );
}

function HeadingRow({ total, onNew, onNewHtml }: { total: number; onNew: () => void; onNewHtml: () => void }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 'var(--space-4)' }}>
      <div>
        <h1 style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-4xl)', fontWeight: 700, color: 'var(--color-text-brand)' }}>
          Templates
        </h1>
        <p style={{ marginTop: 'var(--space-1)', fontSize: 'var(--text-md)', color: 'var(--color-text-muted)' }}>
          {total === 0 ? 'No templates yet.' : `${total} templates · pick a starting point or roll your own.`}
        </p>
      </div>
      <div style={{ display: 'flex', gap: 'var(--space-2)', flexShrink: 0 }}>
        <button
          type="button"
          onClick={onNewHtml}
          data-testid="new-html-template-btn"
          title="Author or paste your own email-safe HTML"
          style={{
            padding: '10px 18px',
            borderRadius: 'var(--btn-radius, 8px)',
            background: '#fff',
            color: 'var(--color-text-secondary, #2b2b3c)',
            border: '1px solid var(--color-border-default, #ececf2)',
            fontFamily: 'inherit',
            fontSize: 14,
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          Start from HTML
        </button>
        <button
          type="button"
          onClick={onNew}
          data-testid="new-template-btn"
          style={{
            padding: '10px 18px',
            borderRadius: 'var(--btn-radius, 8px)',
            background: 'var(--color-action-primary, #1e1b4b)',
            color: '#fff',
            border: 'none',
            fontFamily: 'inherit',
            fontSize: 14,
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          + New template
        </button>
      </div>
    </div>
  );
}

/* ── Filter row ── */

function FilterRow({
  ownerTab,
  onOwnerTab,
  categories,
  onToggleCategory,
  kinds,
  onToggleKind,
  search,
  onSearch,
}: {
  ownerTab: OwnerTab;
  onOwnerTab: (v: OwnerTab) => void;
  categories: Set<TemplateCategory>;
  onToggleCategory: (c: TemplateCategory) => void;
  kinds: Set<TemplateKind>;
  onToggleKind: (k: TemplateKind) => void;
  search: string;
  onSearch: (v: string) => void;
}) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-3)', alignItems: 'center' }}>
      <div role="tablist" style={{ display: 'inline-flex', border: '1px solid var(--color-border-default)', borderRadius: 6, overflow: 'hidden' }}>
        {(['all', 'mine', 'system'] as OwnerTab[]).map((o) => (
          <button
            key={o}
            type="button"
            role="tab"
            aria-selected={ownerTab === o}
            onClick={() => onOwnerTab(o)}
            style={{
              padding: '6px 14px',
              background: ownerTab === o ? 'var(--color-action-primary, #1e1b4b)' : '#fff',
              color: ownerTab === o ? '#fff' : 'var(--color-text-muted)',
              border: 'none',
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            {o === 'all' ? 'All' : o === 'mine' ? 'Mine' : 'System'}
          </button>
        ))}
      </div>

      <div style={{ display: 'flex', gap: 'var(--space-1)', flexWrap: 'wrap' }}>
        {ALL_CATEGORIES.map((c) => (
          <FilterChip key={c} active={categories.has(c)} onClick={() => onToggleCategory(c)}>
            {c.replace(/_/g, ' ').toLowerCase()}
          </FilterChip>
        ))}
      </div>

      <div style={{ display: 'flex', gap: 'var(--space-1)' }}>
        {(['CONTENT_TEMPLATE', 'LAYOUT_STRUCTURE', 'CUSTOM_HTML'] as TemplateKind[]).map((k) => (
          <FilterChip key={k} active={kinds.has(k)} onClick={() => onToggleKind(k)}>
            {KIND_LABELS[k]}
          </FilterChip>
        ))}
      </div>

      <input
        type="search"
        placeholder="Search…"
        value={search}
        onChange={(e) => onSearch(e.target.value)}
        style={{
          marginLeft: 'auto',
          padding: '8px 12px',
          border: '1px solid var(--color-border-default)',
          borderRadius: 6,
          fontFamily: 'inherit',
          fontSize: 13,
          minWidth: 220,
        }}
      />
    </div>
  );
}

function FilterChip({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        padding: '4px 10px',
        borderRadius: 999,
        border: active ? '1px solid var(--color-brand-blue, #3b5bdb)' : '1px solid var(--color-border-default, #ececf2)',
        background: active ? 'var(--color-blue-50, #e8edff)' : '#fff',
        color: active ? 'var(--color-brand-blue, #3b5bdb)' : 'var(--color-text-muted, #6b6b7d)',
        fontSize: 12,
        fontWeight: 600,
        cursor: 'pointer',
        textTransform: 'capitalize',
      }}
    >
      {children}
    </button>
  );
}

/* ── Grid + cards ── */

function Grid({
  rows,
  meUserId,
  onPreview,
  onUse,
  onCustomise,
  onEditOwn,
  onDuplicate,
  onDelete,
}: {
  rows: Template[];
  meUserId: string;
  onPreview: (t: Template) => void;
  onUse: (t: Template) => void;
  onCustomise: (t: Template) => void;
  onEditOwn: (t: Template) => void;
  onDuplicate: (t: Template) => void;
  onDelete: (t: Template) => void;
}) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 20 }}>
      {rows.map((t) => (
        <Card
          key={t.id}
          template={t}
          isOwn={!t.is_system && t.created_by === meUserId}
          onPreview={() => onPreview(t)}
          onUse={() => onUse(t)}
          onCustomise={() => onCustomise(t)}
          onEditOwn={() => onEditOwn(t)}
          onDuplicate={() => onDuplicate(t)}
          onDelete={() => onDelete(t)}
        />
      ))}
    </div>
  );
}

function Card({
  template,
  isOwn,
  onPreview,
  onUse,
  onCustomise,
  onEditOwn,
  onDuplicate,
  onDelete,
}: {
  template: Template;
  isOwn: boolean;
  onPreview: () => void;
  onUse: () => void;
  onCustomise: () => void;
  onEditOwn: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
}) {
  return (
    <article
      data-testid={`gallery-card-${template.id}`}
      style={{
        border: '1px solid var(--color-border-default, #ececf2)',
        borderRadius: 12,
        overflow: 'hidden',
        background: '#fff',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div
        style={{
          position: 'relative',
          height: 240,
          overflow: 'hidden',
          borderBottom: '1px solid var(--color-border-default, #ececf2)',
          background: 'var(--color-surface-muted, #f4f5f9)',
        }}
      >
        <iframe
          title={template.name}
          srcDoc={template.body_html}
          // No scripts/forms/navigation — protects against the rendered
          // template's `{{calendar_url}}` hrefs pulling Engage into the
          // preview when clicked.
          sandbox=""
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: 600,
            height: 800,
            border: 0,
            transform: 'scale(0.45)',
            transformOrigin: 'top left',
            pointerEvents: 'none',
          }}
        />
        {template.is_system && (
          <span
            style={{
              position: 'absolute',
              top: 8,
              right: 8,
              padding: '2px 8px',
              borderRadius: 999,
              background: '#1e1b4b',
              color: '#fff',
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: 0.04,
              textTransform: 'uppercase',
            }}
          >
            System
          </span>
        )}
      </div>
      <div style={{ padding: '14px 16px 16px', display: 'flex', flexDirection: 'column', gap: 8 }}>
        <header style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8 }}>
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: 'var(--color-text-brand, #1e1b4b)' }}>
            {template.name}
          </h3>
          {template.tier && (
            <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', color: 'var(--color-brand-blue, #3b5bdb)' }}>
              {template.tier.toLowerCase()}
            </span>
          )}
        </header>
        <p style={{ margin: 0, fontSize: 12, color: 'var(--color-text-muted, #6b6b7d)' }}>
          {template.category ? template.category.replace(/_/g, ' ').toLowerCase() : '-'}
        </p>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 4 }}>
          <ActionBtn onClick={onPreview}>Preview</ActionBtn>
          <ActionBtn onClick={onUse} variant="primary">Use</ActionBtn>
          {/* "Edit" on a system template clones it into the caller's org
              and opens the builder on the copy. The original system
              template is read-only and unchanged — we ship it as a
              starting point, not a managed asset. */}
          {template.is_system && (
            <ActionBtn onClick={onCustomise} title="Creates your own editable copy">
              Edit
            </ActionBtn>
          )}
          {/* Own templates open the builder directly. Editing doesn't
              affect campaigns currently using this template — the body
              is snapshotted onto the campaign's sequence step when the
              template is picked. Future campaigns get the new version. */}
          {isOwn && (
            <ActionBtn
              onClick={onEditOwn}
              title="Editing this template won't change campaigns already using it"
            >
              Edit
            </ActionBtn>
          )}
          {isOwn && <ActionBtn onClick={onDuplicate}>Duplicate</ActionBtn>}
          {isOwn && <ActionBtn onClick={onDelete} variant="danger">Delete</ActionBtn>}
        </div>
      </div>
    </article>
  );
}

function ActionBtn({
  children,
  onClick,
  variant = 'default',
  title,
}: {
  children: React.ReactNode;
  onClick: () => void;
  variant?: 'default' | 'primary' | 'danger';
  title?: string;
}) {
  const styles = {
    default: { bg: '#fff', color: 'var(--color-text-secondary, #2b2b3c)', border: '1px solid var(--color-border-default, #ececf2)' },
    primary: { bg: 'var(--color-brand-blue, #3b5bdb)', color: '#fff', border: 'none' },
    danger: { bg: '#fff', color: 'var(--color-danger, #e03131)', border: '1px solid var(--color-danger-border, #ffd6d6)' },
  }[variant];
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      style={{
        padding: '4px 10px',
        borderRadius: 6,
        background: styles.bg,
        color: styles.color,
        border: styles.border,
        fontSize: 12,
        fontWeight: 600,
        cursor: 'pointer',
      }}
    >
      {children}
    </button>
  );
}

/* ── Empty / error / loading ── */

function EmptyState({ onClear, hasAny }: { onClear: () => void; hasAny: boolean }) {
  return (
    <div style={{ padding: 48, textAlign: 'center', border: '1px dashed var(--color-border-default)', borderRadius: 12, color: 'var(--color-text-muted)' }}>
      <p style={{ fontSize: 16, fontWeight: 600, margin: 0, color: 'var(--color-text-brand)' }}>
        {hasAny ? 'No templates match your filters.' : 'No templates yet.'}
      </p>
      <p style={{ marginTop: 8 }}>
        {hasAny ? 'Try clearing them to see all templates.' : 'Pick a starting point with "New template".'}
      </p>
      {hasAny && (
        <button
          type="button"
          onClick={onClear}
          style={{ marginTop: 12, padding: '6px 14px', borderRadius: 6, border: '1px solid var(--color-border-default)', background: '#fff', cursor: 'pointer' }}
        >
          Clear filters
        </button>
      )}
    </div>
  );
}

function ErrorState({ onRetry }: { onRetry: () => void }) {
  return (
    <div style={{ padding: 32, textAlign: 'center', border: '1px solid var(--color-danger-border, #ffd6d6)', borderRadius: 12, background: '#fff7f7' }}>
      <p style={{ margin: 0, color: 'var(--color-danger, #e03131)', fontWeight: 600 }}>Couldn't load templates.</p>
      <button type="button" onClick={onRetry} style={{ marginTop: 12, padding: '6px 14px', borderRadius: 6, border: 'none', background: 'var(--color-brand-blue, #3b5bdb)', color: '#fff', cursor: 'pointer' }}>
        Retry
      </button>
    </div>
  );
}

function LoadingGrid() {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 20 }}>
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} style={{ height: 360, borderRadius: 12, background: 'var(--color-surface-muted, #f4f5f9)' }} />
      ))}
    </div>
  );
}

function PreviewModal({ template, onClose }: { template: Template; onClose: () => void }) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 200,
        padding: 24,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ background: '#fff', borderRadius: 12, maxWidth: 700, width: '100%', maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}
      >
        <header style={{ padding: '14px 20px', borderBottom: '1px solid var(--color-border-default)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <strong>{template.name}</strong>
          <button type="button" onClick={onClose} aria-label="Close" style={{ border: 'none', background: 'transparent', fontSize: 20, cursor: 'pointer' }}>
            ✕
          </button>
        </header>
        <iframe title={`${template.name} preview`} srcDoc={template.body_html} sandbox="" style={{ width: '100%', height: '80vh', border: 0 }} />
      </div>
    </div>
  );
}
